import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, CheckCircle, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", product_interest: "", message: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "Valid email is required";
    if (!form.phone.trim() || form.phone.trim().length < 8) errs.phone = "Valid phone number is required";
    if (!form.message.trim()) errs.message = "Message is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setStatus(null);

    const subject = encodeURIComponent(`Inquiry from ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nCompany: ${form.company || "N/A"}\nProduct Interest: ${form.product_interest || "N/A"}\n\nMessage:\n${form.message}`
    );

    window.location.href = `mailto:jsypp.2018@gmail.com?subject=${subject}&body=${body}`;
    setStatus("success");
    setForm({ name: "", email: "", phone: "", company: "", product_interest: "", message: "" });
    setLoading(false);
  };

  const update = (field, val) => {
    setForm((p) => ({ ...p, [field]: val }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: undefined }));
  };

  return (
    <div data-testid="contact-page" className="pt-16">
      <SEO
        title="Contact JSY Printing & Packaging"
        description="Contact JSY for BOPP bags, polyester sheets, and readymade packaging requirements. Reach our Kolkata facility for quotations and bulk orders."
        image="/images/about-factory-gate.png"
      />
      {/* Hero */}
  <section className="py-12 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="overline">Contact Us</span>
            <h1 className="mt-3 text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              Let's <span className="text-[#1D4ED8]">Connect</span>
            </h1>
            <p className="mt-4 text-[#71717A] max-w-lg">
              Ready to discuss your packaging and printing needs? Get in touch and we'll respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="lg:col-span-3">
            <h2 className="text-2xl tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              Send an Inquiry
            </h2>

            {status === "success" && (
              <div data-testid="success-msg" className="mb-6 p-4 bg-[#F0FDF4] border border-[#16A34A]/20 rounded flex items-center gap-3">
                <CheckCircle size={18} className="text-[#16A34A]" />
                <p className="text-sm text-[#16A34A]">Your email draft is ready. Please send it from your mail app and we'll get back to you soon.</p>
              </div>
            )}
            {status === "error" && (
              <div data-testid="error-msg" className="mb-6 p-4 bg-red-50 border border-red-200 rounded flex items-center gap-3">
                <AlertCircle size={18} className="text-red-500" />
                <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
              </div>
            )}

            <form data-testid="inquiry-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Name *</label>
                  <input data-testid="input-name" className={`form-input ${errors.name ? 'border-red-400' : ''}`} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your full name" />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Email *</label>
                  <input data-testid="input-email" type="email" className={`form-input ${errors.email ? 'border-red-400' : ''}`} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="email@company.com" />
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Phone *</label>
                  <input data-testid="input-phone" className={`form-input ${errors.phone ? 'border-red-400' : ''}`} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 7003670034" />
                  {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Company</label>
                  <input data-testid="input-company" className="form-input" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Company name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Product Interest</label>
                <select data-testid="input-product" className="form-input" value={form.product_interest} onChange={(e) => update("product_interest", e.target.value)}>
                  <option value="">Select a product category</option>
                  <option value="BOPP Bags">BOPP Bags</option>
                  <option value="Polyester Sheets">Polyester Sheets</option>
                  <option value="Readymade Packaging">Readymade Packaging</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#71717A] font-medium tracking-wide uppercase mb-2">Message *</label>
                <textarea data-testid="input-message" className={`form-input form-textarea ${errors.message ? 'border-red-400' : ''}`} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your requirements..." />
                {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
              </div>
              <button data-testid="submit-inquiry-button" type="submit" className="btn-primary" disabled={loading}>
                {loading ? "Sending..." : <><Send size={16} /> Send Inquiry</>}
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2} variants={fadeUp} className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-lg tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                Contact Information
              </h3>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] flex-shrink-0">
                    <Phone size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-[#71717A] mt-1">+91 7003670034</p>
                    <p className="text-sm text-[#71717A]">+91 7890986920</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] flex-shrink-0">
                    <Mail size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-[#71717A] mt-1">jsypp.2018@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] flex-shrink-0">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Factory Address</p>
                    <p className="text-sm text-[#71717A] mt-1">
                      I5-150/NEW, B.B.T. Road,<br />
                      Budge Budge Trunk Rd, Maheshtala,<br />
                      Kolkata, West Bengal 700141
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div data-testid="google-map" className="rounded overflow-hidden border border-[#E4E4E7]" style={{ height: '260px' }}>
              <iframe
                src="https://www.google.com/maps?q=JSY+PRINTING+%26+PACKAGING+Maheshtala+Kolkata+700141&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="JSY Printing & Packaging Location"
              ></iframe>
            </div>

            {/* Business Hours */}
            <div className="p-6 bg-[#F4F5F7] rounded">
              <h4 className="text-sm font-medium mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Business Hours</h4>
              <div className="space-y-2 text-sm text-[#71717A]">
                <div className="flex justify-between"><span>Mon - Fri</span><span className="font-medium text-[#09090B]">9:00 AM - 7:00 PM</span></div>
                <div className="flex justify-between"><span>Saturday</span><span className="font-medium text-[#09090B]">9:00 AM - 4:00 PM</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="text-[#71717A]">Closed</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
